import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useSubscription } from '../App'

const FREE_CHART_LIMIT = 3

interface ChartSummary {
  id: string
  title: string
  is_public: boolean
  created_at: string
  updated_at: string
  chapters: {
    book: string
    chapter_num: number
  }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const isSubscriber = useSubscription()
  const [charts, setCharts] = useState<ChartSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [userName, setUserName] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      setUserName(user?.user_metadata?.full_name || user?.email || '')

      const { data, error } = await supabase
        .from('charts')
        .select('id, title, is_public, created_at, updated_at, chapters(book, chapter_num)')
        .order('updated_at', { ascending: false })

      if (error) throw error
      setCharts((data as any) || [])
    } catch (err) {
      console.error('Error loading charts:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this chart?')) return
    await supabase.from('charts').delete().eq('id', id)
    setCharts(prev => prev.filter(c => c.id !== id))
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  return (
    <div style={styles.page}>
      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.navLeft}>
          <h1 style={styles.navTitle}>Chapter Chart</h1>
        </div>
        <div style={styles.navCenter}>
          <a style={styles.navToolLink} href="https://disputation.app" target="_blank" rel="noopener" title="Disputation — Bible Search by Doctrine">📚 Disputation</a>
          <a style={styles.navToolLink} href="https://disputation.app/BibleCharting/index.html" target="_blank" rel="noopener" title="Arc &amp; Diagram Tool">⤢ Arc Tool</a>
        </div>
        <div style={styles.navRight}>
          <span style={styles.navUser}>{userName}</span>
          <button style={styles.navBtn} onClick={handleSignOut}>Sign Out</button>
        </div>
      </nav>

      {/* Content */}
      <main style={styles.main}>
        <div style={styles.topRow}>
          <h2 style={styles.sectionTitle}>My Charts</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' as const }}>
            <button style={styles.exampleBtn} onClick={() => navigate('/example')}>
              Chapter Chart (Romans 1)
            </button>
            <button style={styles.exampleBtn} onClick={() => navigate('/book-example')}>
              Book Chart (Romans)
            </button>
            {!isSubscriber && charts.length >= FREE_CHART_LIMIT ? (
              <a
                href="https://disputation.app/#settings"
                target="_blank"
                rel="noopener"
                style={styles.upgradeBtn}
                title={`Free accounts are limited to ${FREE_CHART_LIMIT} charts. Subscribe to create unlimited charts.`}
              >
                🔒 Subscribe for More Charts
              </a>
            ) : (
              <button style={styles.newBtn} onClick={() => navigate('/chart/new')}>
                + New Chart
              </button>
            )}
          </div>
        </div>

        {!isSubscriber && charts.length > 0 && (
          <div style={styles.upgradeBanner}>
            <span>
              {charts.length >= FREE_CHART_LIMIT
                ? `You've reached the ${FREE_CHART_LIMIT}-chart limit on the free plan.`
                : `Free plan: ${charts.length} of ${FREE_CHART_LIMIT} charts used.`}
              {' '}
            </span>
            <a href="https://disputation.app/#settings" target="_blank" rel="noopener" style={styles.upgradeBannerLink}>
              Subscribe at disputation.app
            </a>
            {' '}for unlimited charts, sharing, and Codex Fill.
          </div>
        )}

        {loading ? (
          <p style={styles.emptyText}>Loading...</p>
        ) : charts.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No charts yet.</p>
            <p style={styles.emptySubtext}>
              Create your first chapter analysis using Adler's framework.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' as const }}>
              <button style={styles.exampleBtn} onClick={() => navigate('/example')}>
                Chapter Chart (Romans 1)
              </button>
              <button style={styles.exampleBtn} onClick={() => navigate('/book-example')}>
                Book Chart (Romans)
              </button>
              <button style={styles.newBtn} onClick={() => navigate('/chart/new')}>
                + New Chart
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.grid}>
            {charts.map(chart => (
              <div key={chart.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <div>
                    <h3 style={styles.cardTitle}>
                      {chart.chapters?.book} {chart.chapters?.chapter_num}
                    </h3>
                    <p style={styles.cardSubtitle}>{chart.title || 'Untitled'}</p>
                  </div>
                  {chart.is_public && (
                    <span style={styles.publicBadge}>Public</span>
                  )}
                </div>
                <p style={styles.cardDate}>Updated {formatDate(chart.updated_at)}</p>
                <div style={styles.cardActions}>
                  <button
                    style={styles.openBtn}
                    onClick={() => navigate(`/chart/${chart.id}`)}
                  >
                    Open
                  </button>
                  <button
                    style={styles.deleteBtn}
                    onClick={() => handleDelete(chart.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Adler Reminder */}
        <div style={styles.adlerBox}>
          <h3 style={styles.adlerTitle}>Adler's Three Stages</h3>
          <div style={styles.adlerStages}>
            <div style={styles.adlerStage}>
              <span style={styles.adlerNum}>1</span>
              <div>
                <strong>Structural</strong>
                <p>Classification · Unity · Problem</p>
              </div>
            </div>
            <div style={styles.adlerStage}>
              <span style={styles.adlerNum}>2</span>
              <div>
                <strong>Interpretive</strong>
                <p>Key Terms · Propositions · Paragraphs</p>
              </div>
            </div>
            <div style={styles.adlerStage}>
              <span style={styles.adlerNum}>3</span>
              <div>
                <strong>Critical</strong>
                <p>Evaluation · Agreement · Application</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 32px',
    background: '#1e3a5f',
    color: 'white',
  },
  navLeft: {},
  navCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  navToolLink: {
    padding: '5px 12px',
    borderRadius: '5px',
    border: '1px solid rgba(255,255,255,0.25)',
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.85)',
    fontSize: '13px',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap' as const,
  },
  navTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  navUser: {
    fontSize: '14px',
    opacity: 0.8,
  },
  navBtn: {
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '6px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px 24px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '22px',
    color: '#1e3a5f',
  },
  newBtn: {
    padding: '10px 20px',
    background: '#1e3a5f',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  exampleBtn: {
    padding: '10px 20px',
    background: 'white',
    color: '#1e3a5f',
    border: '1px solid #1e3a5f',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px',
    marginBottom: '40px',
  },
  card: {
    background: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  cardTitle: {
    margin: '0 0 4px',
    fontSize: '18px',
    color: '#1e3a5f',
  },
  cardSubtitle: {
    margin: 0,
    fontSize: '12px',
    color: '#888',
  },
  publicBadge: {
    fontSize: '11px',
    background: '#dcfce7',
    color: '#16a34a',
    padding: '2px 8px',
    borderRadius: '20px',
    fontWeight: '600',
  },
  cardDate: {
    fontSize: '12px',
    color: '#aaa',
    margin: '0 0 14px',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  openBtn: {
    flex: 1,
    padding: '8px',
    background: '#1e3a5f',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  deleteBtn: {
    padding: '8px 12px',
    background: 'white',
    color: '#dc2626',
    border: '1px solid #fca5a5',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    marginBottom: '40px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#888',
    margin: '0 0 8px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#aaa',
    marginBottom: '20px',
  },
  upgradeBtn: {
    padding: '10px 16px',
    background: '#fef9c3',
    color: '#92400e',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap' as const,
  },
  upgradeBanner: {
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '13px',
    color: '#78350f',
    marginBottom: '20px',
  },
  upgradeBannerLink: {
    color: '#1e3a5f',
    fontWeight: '600',
  },
  adlerBox: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #e2e8f0',
  },
  adlerTitle: {
    margin: '0 0 16px',
    fontSize: '15px',
    color: '#1e3a5f',
    fontWeight: '700',
  },
  adlerStages: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap' as const,
  },
  adlerStage: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    flex: 1,
    minWidth: '180px',
  },
  adlerNum: {
    width: '28px',
    height: '28px',
    background: '#1e3a5f',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
}
